import { Globe2, PackageCheck, Ship, Truck } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { CAPABILITY_IMAGES, LOGISTICS_IMAGES } from "../data/content";

const capabilities = [
  { icon: Globe2, title: "Global sourcing", text: "Relationships with manufacturers, mills, and suppliers across the UK, Europe, USA, Far East, and Sub-Continent." },
  { icon: PackageCheck, title: "Specification certainty", text: "Equipment supplied to correct specifications, on time, and within budget." },
  { icon: Truck, title: "Downstream logistics", text: "Importation, warehousing, bulk purchase, sales, and distribution of refined petroleum products." },
  { icon: Ship, title: "Maritime reach", text: "Regional knowledge combined with global project delivery capability." },
];

export default function Capabilities() {
  return (
    <main data-testid="capabilities-page" className="bg-[#070C14] text-white">
      <PageHero
        testId="capabilities-hero"
        eyebrow="Capabilities & reach"
        title="What we serve, where we connect, how we deliver."
        description="A cross-industry operating platform spanning energy, power, renewables, governance, logistics, procurement, and maritime infrastructure."
        image="/assets/page_17_img_1.jpeg"
        imageAlt="Tanker truck driving beside water"
      />

      <section data-testid="industries-served-section" className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-10">
        <Reveal className="mb-12 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.32em] text-amber-400">What we serve</p>
            <h2 className="mt-4 font-heading text-3xl font-bold sm:text-5xl">Industrial range, editorial focus.</h2>
          </div>
          <p className="max-w-md text-sm leading-7 text-slate-400">Extracted directly from the corporate profile's industry montage and reframed for the web.</p>
        </Reveal>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {CAPABILITY_IMAGES.map((item, index) => (
            <Reveal key={item.label} delay={index * 0.06} className="group relative h-80 overflow-hidden rounded-3xl border border-white/10">
              <img src={item.src} alt={item.label} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#070C14] via-transparent to-transparent" />
              <h3 className="absolute bottom-6 left-6 font-heading text-2xl font-semibold" data-testid={`capability-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>{item.label}</h3>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="supply-chain-section" className="border-y border-white/10 bg-[#0D131F] px-4 py-24 sm:px-8 lg:px-10">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[.8fr_1.2fr]">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.32em] text-blue-400">Supply chain</p>
            <h2 className="mt-4 font-heading text-3xl font-bold sm:text-5xl">A global value web with local execution discipline.</h2>
          </Reveal>
          <div className="grid gap-5 sm:grid-cols-2">
            {capabilities.map((item, index) => (
              <Reveal key={item.title} delay={index * 0.06} className="rounded-3xl border border-white/10 bg-white/[0.04] p-7">
                <item.icon className="text-amber-400" size={27} />
                <h3 className="mt-8 font-heading text-2xl font-semibold">{item.title}</h3>
                <p className="mt-4 text-sm leading-7 text-slate-400">{item.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="logistics-gallery-section" className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-10">
        <Reveal className="mb-12">
          <p className="font-mono text-xs uppercase tracking-[0.32em] text-amber-400">Logistics library</p>
          <h2 className="mt-4 font-heading text-3xl font-bold sm:text-5xl">Real profile imagery, clipped with intent.</h2>
        </Reveal>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {LOGISTICS_IMAGES.map((src, index) => (
            <Reveal key={src} delay={index * 0.04} className={`${index % 3 === 0 ? "clip-frame" : "rounded-3xl"} h-52 overflow-hidden border border-white/10 md:h-64`}>
              <img src={src} alt={`Clach logistics and procurement asset ${index + 1}`} className="h-full w-full object-cover transition-transform duration-700 hover:scale-110" />
            </Reveal>
          ))}
        </div>
      </section>
    </main>
  );
}
